import { create, StateCreator } from 'zustand';
import { createSelectors } from '@/utils/helpers';
import { OrganType } from '@/api/apis/case';

export type SchedulerControlFilters = {
  statusFilters: boolean;
  shiftTotals: boolean;
  resources: string[];
  organs: OrganType[];
  phoneSupportOrgans: OrganType[];
  shadowOrgans: OrganType[];
  proctorOrgans: OrganType[];
  hubs: string[];
};

export const initialSchedulerControlFilters: SchedulerControlFilters = {
  shiftTotals: false,
  statusFilters: false,
  resources: [],
  organs: [],
  phoneSupportOrgans: [],
  shadowOrgans: [],
  proctorOrgans: [],
  hubs: [],
};

type SchedulerFiltersStoreAction = {
  setFilterByKey: <K extends keyof SchedulerControlFilters, V extends SchedulerControlFilters[K]>(
    key: K,
    value: V,
  ) => void;
  clearFilters: () => void;
};

type SchedulerFiltersStoreType = SchedulerControlFilters & SchedulerFiltersStoreAction;

const initializer: StateCreator<SchedulerFiltersStoreType> = (set) => {
  return {
    ...initialSchedulerControlFilters,
    setFilterByKey: (key, value) => set({ [key]: value }),
    clearFilters: () => set(initialSchedulerControlFilters),
  };
};

export const createSchedulerFiltersStore = () => createSelectors(create<SchedulerFiltersStoreType>(initializer));

const hasActiveFilters = (state: SchedulerFiltersStoreType) => {
  const { statusFilters, shiftTotals, resources, organs, phoneSupportOrgans, hubs, proctorOrgans, shadowOrgans } =
    state;
  return (
    statusFilters ||
    shiftTotals ||
    resources.length > 0 ||
    organs.length > 0 ||
    phoneSupportOrgans.length > 0 ||
    proctorOrgans.length > 0 ||
    shadowOrgans.length > 0 ||
    hubs.length > 0
  );
};

const hasResourceFilters = (state: SchedulerFiltersStoreType) => {
  const { resources, organs, phoneSupportOrgans, hubs, proctorOrgans, shadowOrgans } = state;
  return (
    resources.length > 0 ||
    organs.length > 0 ||
    phoneSupportOrgans.length > 0 ||
    hubs.length > 0 ||
    proctorOrgans.length > 0 ||
    shadowOrgans.length > 0
  );
};

export const scheduleFiltersSelectors = {
  hasActiveFilters,
  hasResourceFilters,
};

export type BaseSchedulerHighlightValue = {
  resourceId?: string;
  day?: Date;
};

export type SchedulerHighlightState<T extends BaseSchedulerHighlightValue> = {
  value: T | null;
};

export type SchedulerHighlightAction<T extends BaseSchedulerHighlightValue> = {
  setHighlightValue: (value: T) => void;
};

type SchedulerHighlightStoreType<T extends BaseSchedulerHighlightValue> = SchedulerHighlightState<T> &
  SchedulerHighlightAction<T>;

export const createSchedulerHighlightStore = <T extends Record<string, unknown>>() => {
  let timerId: NodeJS.Timeout | undefined;

  return createSelectors(
    create<SchedulerHighlightStoreType<T>>()((set) => ({
      value: null,
      setHighlightValue: (value) => {
        set({ value });
        if (timerId) clearTimeout(timerId);
        timerId = setTimeout(() => set({ value: null }), 3000);
      },
    })),
  );
};
